import { v4 } from 'uuid'
import { idMapper, jsonClone } from 'web/utils/utils'
import { FightSetup, FullFight } from './models'

/**
 * Clones the given fight and list of setups, giving them all new unique IDs.
 */
export function cloneFight(params: {
    fullFight: FullFight
    setups: FightSetup[]
}): {
    clonedFullFight: FullFight
    clonedSetups: FightSetup[]
} {

    const segmentIdMapper = idMapper(() => v4())
    const eventIdMapper = idMapper(() => v4())

    const { fullFight, setups } = params

    const clonedFullFight = jsonClone(fullFight)
    const newFightId = v4()

    clonedFullFight.fight.id = newFightId
    clonedFullFight.fight.createdAt = new Date().toISOString()
    clonedFullFight.fight.updatedAt = new Date().toISOString()

    clonedFullFight.segments.forEach(segment => {
        segment.fightId = newFightId
        segment.id = segmentIdMapper.newId(segment)
    })

    // We need to process children as a separate loop after the first loop
    clonedFullFight.segments.forEach(segment => {
        if (segment.childId != null) {
            segment.childId = segmentIdMapper.swap(segment.childId)
        }
    })

    clonedFullFight.events.forEach(event => {
        event.id = eventIdMapper.newId(event)
        event.fightSegmentId = segmentIdMapper.swap(event.fightSegmentId)
    })

    const clonedSetups = setups.map(setup => {
        return cloneSetup({
            setup,
            newFightId: newFightId
        })
    })

    clonedSetups.forEach(data => {
        const { clonedSetup } = data

        /*
            We need to re-map any assignments assigned to fight
            events. Assignments assigned to setup fight events are
            already taken care of at this point.
        */

        const fightEventAssignments = [
            ...clonedSetup.spellAssignments.filter(item => {
                return eventIdMapper.containsOldId(item.fightEventId)
            }),
            ...clonedSetup.teamAssignments.filter(item => {
                return eventIdMapper.containsOldId(item.fightEventId)
            })
        ]

        fightEventAssignments.forEach(item => {
            item.fightEventId = eventIdMapper.swap(item.fightEventId)
        })

        clonedSetup.setupFightEvents.forEach(setupEvent => {
            setupEvent.fightSegmentId = segmentIdMapper.swap(setupEvent.fightSegmentId)
        })
    })

    return {
        clonedFullFight,
        clonedSetups: clonedSetups.map(data => data.clonedSetup)
    }
}

/**
 * Clones the given setup, optionally moving the clone to a new fight if an ID
 * is passed.
 */
export function cloneSetup(params: {
    setup: FightSetup
    newFightId?: string
}): {
    clonedSetup: FightSetup
} {

    const { setup, newFightId } = params

    const clonedSetup = jsonClone(setup)

    clonedSetup.id = v4()
    clonedSetup.fightId = newFightId ?? clonedSetup.fightId
    clonedSetup.createdAt = new Date().toISOString()
    clonedSetup.updatedAt = new Date().toISOString()

    clonedSetup.setupFightEvents.forEach(ev => {
        ev.setupId = clonedSetup.id
    })

    clonedSetup.fightPlayers.forEach(player => {

        // This is just a unique ID for the player in the setup, there
        // is no connection to any potential roster player we need to
        // maintain.
        const oldPlayerId = player.id
        const newPlayerId = v4()
        player.id = newPlayerId

        clonedSetup.spellAssignments.forEach(sa => {
            if (sa.playerId === oldPlayerId) {
                sa.playerId = newPlayerId
            }
        })

    })

    const setupEventsIdMapper = idMapper(() => v4())

    /*
        The setup is responsible for re-mapping any assignments
        assigned to its events. The fight cloner will take care of
        re-mapping any assignments assigned to fight events.
    */

    clonedSetup.setupFightEvents.forEach(setupEvent => {
        setupEvent.id = setupEventsIdMapper.newId(setupEvent)
    })

    const setupFightEventAssignments = [
        ...clonedSetup.spellAssignments.filter(item => {
            return setupEventsIdMapper.containsOldId(item.fightEventId)
        }),
        ...clonedSetup.teamAssignments.filter(item => {
            return setupEventsIdMapper.containsOldId(item.fightEventId)
        })
    ]

    setupFightEventAssignments.forEach(item => {
        item.fightEventId = setupEventsIdMapper.swap(item.fightEventId)
    })

    return {
        clonedSetup: clonedSetup
    }
}
